import { FunnelContact } from 'features/co-pilot/co-pilot.types';

export type SalesColumnId = 'answered' | 'bookedMeeting' | 'qualifiedBant' | 'customer' | 'lost';
export type RecruitingColumnId =
  | 'answered'
  | 'bookedMeeting'
  | 'interviewStage'
  | 'hired'
  | 'lost'
  | 'interviewStageInt'
  | 'cvToClient'
  | 'clientInterview';

export type ColumnId = SalesColumnId | RecruitingColumnId;

export const salesColumns: SalesColumnId[] = ['answered', 'bookedMeeting', 'qualifiedBant', 'customer', 'lost'];
export const internalRecruitingColumns: RecruitingColumnId[] = ['answered', 'bookedMeeting', 'interviewStage', 'hired', 'lost'];
export const externalRecruitingColumns: RecruitingColumnId[] = [
  'answered',
  'bookedMeeting',
  'interviewStageInt',
  'cvToClient',
  'clientInterview',
  'hired',
  'lost',
];

export type PipelineSegment = 'sales' | 'internalRecruiting' | 'externalRecruiting';

// Accent colour per pipeline stage – drives the coloured bar above each board column.
// Early stages start on a light violet and darken towards the primary colour, closing
// stages are green, lost is neutral grey.
export const columnAccents: Record<ColumnId, string> = {
  answered: '#B0A9FF',
  bookedMeeting: '#8B8BF8',
  qualifiedBant: '#4F44FF',
  interviewStage: '#4F44FF',
  interviewStageInt: '#4F44FF',
  cvToClient: '#4F44FF',
  clientInterview: '#4F44FF',
  customer: '#18C61F',
  hired: '#18C61F',
  lost: '#C9CCD3',
};

export function getColumnAccent(columnId: ColumnId): string {
  return columnAccents[columnId] ?? '#B0A9FF';
}

export function getColumnsForSegment(segment: PipelineSegment): ColumnId[] {
  switch (segment) {
    case 'sales':
      return salesColumns;
    case 'internalRecruiting':
      return internalRecruitingColumns;
    case 'externalRecruiting':
      return externalRecruitingColumns;
    default:
      return [];
  }
}

// Inspect a contact and decide its current status for a pipeline segment.
export function getContactStatus(contact: FunnelContact, segment: PipelineSegment): ColumnId {
  const pipeline = (contact as any).pipeline?.[segment] || {};
  const columns = getColumnsForSegment(segment);
  // iterate columns from last to first to find the last true flag
  for (let i = columns.length - 1; i >= 0; i--) {
    const col = columns[i];
    if (pipeline?.[col]) return col as ColumnId;
  }
  return 'answered';
}

export function buildColumns(contacts: FunnelContact[], segment: PipelineSegment): Record<ColumnId, FunnelContact[]> {
  const columnIds = getColumnsForSegment(segment);
  const result: Record<ColumnId, FunnelContact[]> = {} as any;
  columnIds.forEach((id) => (result[id] = []));

  contacts.forEach((contact) => {
    const status = getContactStatus(contact, segment);
    if (!result[status]) result[status] = [];
    result[status].push(contact);
  });

  return result;
}